import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { paymentsAPI } from '../services/PaymentApi';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Stripe Checkout mounted inside our own page instead of a redirect.
 *
 * The redirect flow sends the browser to stripe.com and hopes it comes back.
 * That is survivable on the web and poor in the native apps, where the webview
 * leaves the app entirely and the return has to be reconstructed from a query
 * string (see rememberedReturn in PaymentStripeHooks). Embedded Checkout keeps
 * the payer on the page, so there is no navigation to recover from.
 *
 * The server decides nothing about the UI here — the client asks for
 * uiMode:'embedded' because it can render it, and an older build that asks for
 * nothing still gets the redirect. That is what makes this safe to roll out
 * behind a flag with both paths live at once.
 *
 * loadStripe is called with the publishable key the API returns rather than one
 * baked into the bundle, so the key always matches the mode of the secret key
 * the server used to create the session. A test session can never be mounted
 * with a live key.
 */
export default function PaymentEmbeddedCheckout({
  kind,
  registrationIds,
  returnPath,
  onError,
  onClose,
}) {
  const { theme } = useTheme();
  const [publishableKey, setPublishableKey] = useState(null);
  const [failed, setFailed] = useState(null);

  // Stripe calls fetchClientSecret itself, and calls it again when it needs a
  // fresh session. It must stay referentially stable or the provider remounts
  // and the payer loses whatever they had typed, so the arguments are held in a
  // ref rather than closed over.
  const args = useRef({ kind, registrationIds, returnPath });
  args.current = { kind, registrationIds, returnPath };

  // Callbacks come in as inline arrow functions from the modal, so their
  // identity changes on every render. Depended on directly they re-ran the
  // effect and rebuilt `options`, which remounts EmbeddedCheckoutProvider —
  // the screen flickered as if reloading, and each remount created another
  // Checkout Session. Held in a ref, the effect and options stay stable while
  // the callbacks still see current props.
  const cb = useRef({ onError, onClose });
  cb.current = { onError, onClose };

  const fetchClientSecret = useCallback(async () => {
    const { clientSecret, publishableKey: pk } = await paymentsAPI.createCheckoutSession({
      ...args.current,
      uiMode: 'embedded',
    });
    if (!clientSecret) throw new Error('Stripe did not return a client secret');
    // Setting state here rather than in a separate request avoids creating two
    // Checkout Sessions just to learn the key — one call answers both.
    setPublishableKey((prev) => prev || pk || '');
    return clientSecret;
  }, []);

  // The provider cannot mount until the key is known, and the key only arrives
  // with the first session. So the first fetch happens here, and Stripe reuses
  // its result through fetchClientSecret's own cache.
  const firstSecret = useRef(null);
  useEffect(() => {
    let cancelled = false;
    fetchClientSecret()
      .then((secret) => { if (!cancelled) firstSecret.current = secret; })
      .catch((err) => {
        if (cancelled) return;
        setFailed(err?.message || 'Could not start card payment');
        cb.current.onError?.(err);
      });
    return () => { cancelled = true; };
  }, [fetchClientSecret]);

  const stripePromise = useMemo(
    () => (publishableKey ? loadStripe(publishableKey) : null),
    [publishableKey],
  );

  const options = useMemo(() => ({
    fetchClientSecret: async () => firstSecret.current || fetchClientSecret(),
    onComplete: () => {
      // Deliberately does NOT settle anything. The webhook is the source of
      // truth for payment state; this only closes the sheet. Treating an
      // onComplete as proof of payment would mark a booking paid on a card that
      // later failed.
      cb.current.onClose?.();
    },
  }), [fetchClientSecret]);

  if (failed) {
    return (
      <div className={`p-6 text-center ${theme?.text || ''}`}>
        <p className="mb-4">{failed}</p>
        <button
          type="button"
          onClick={() => cb.current.onClose?.()}
          className="px-5 py-2 rounded-lg bg-gray-200 dark:bg-gray-700"
        >
          Close
        </button>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="p-10 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    // The height has to land on the element Stripe actually mounts into, not on
    // an ancestor. EmbeddedCheckout renders its own div, and its iframe starts
    // at zero height and grows from a resize message — so with the height only
    // on a wrapper the container collapsed and the form rendered blank while
    // Stripe reported it had mounted.
    <div className="w-full">
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout className="min-h-[560px] w-full" />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
